import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SchoolIcon from '@mui/icons-material/School';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import type { Plan } from '../../services/billingService';
import billingService from '../../services/billingService';
import { getApiErrorInfo } from '../../services/api';
import type { AddonSeatSummary } from '../../services/addonAssignmentService';

const ADDON_META: Record<string, { label: string; IconComponent: typeof SchoolIcon; description: string }> = {
  'OnDemand-Module-Yearly': {
    label: 'On-Demand Courses',
    IconComponent: SchoolIcon,
    description: 'Self-paced CPE courses available anytime',
  },
  'Webinars-Module-Yearly': {
    label: 'Live Webinars',
    IconComponent: VideoLibraryIcon,
    description: 'Live and recorded webinar sessions',
  },
  'Enterprise-OnDemand-Module-Yearly': {
    label: 'On-Demand Courses',
    IconComponent: SchoolIcon,
    description: 'Full library access for your organization members',
  },
  'Enterprise-Webinar-Module-Yearly': {
    label: 'Live Webinars',
    IconComponent: VideoLibraryIcon,
    description: 'Live webinar access for your organization members',
  },
};

function getAddonMeta(id: string) {
  return (
    ADDON_META[id] ?? {
      label: id.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
      IconComponent: SchoolIcon,
      description: '',
    }
  );
}

export interface ActiveModuleManagerProps {
  addonType: 'individual' | 'enterprise';
  activeAddonIds: string[];
  onApplyChanges: (addonIds: string[], seatCounts: Record<string, number>) => Promise<void>;
  isApplying?: boolean;
  currentSeatCounts?: Record<string, number>;
  seatSummaries?: AddonSeatSummary[];
}

export default function ActiveModuleManager({
  addonType,
  activeAddonIds,
  onApplyChanges,
  isApplying = false,
  currentSeatCounts = {},
  seatSummaries = [],
}: ActiveModuleManagerProps) {
  const [allAddons, setAllAddons] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [pendingAddonIds, setPendingAddonIds] = useState<string[]>(activeAddonIds);
  const [pendingSeatCounts, setPendingSeatCounts] = useState<Record<string, number>>(currentSeatCounts);

  const showSeatManagement = addonType === 'enterprise';
  const initialAddonSet = new Set(activeAddonIds);

  useEffect(() => {
    loadAddons();
  }, [addonType]);

  const loadAddons = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const addons =
        addonType === 'individual'
          ? await billingService.getAvailableAddons()
          : await billingService.getAvailableTenantAddons();
      setAllAddons(addons.filter((a) => a.period === 'year'));
    } catch {
      setLoadError('Failed to load available modules. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const currentModules = allAddons.filter((a) => pendingAddonIds.includes(a.id));
  const availableToAdd = allAddons.filter((a) => !pendingAddonIds.includes(a.id));

  const addedIds = pendingAddonIds.filter((id) => !initialAddonSet.has(id));
  const seatChanges: Array<[string, number]> = showSeatManagement
    ? (Object.entries(pendingSeatCounts).filter(
        ([id, count]) => pendingAddonIds.includes(id) && count !== (currentSeatCounts[id] ?? 0)
      ) as Array<[string, number]>)
    : [];
  const hasChanges = addedIds.length > 0 || seatChanges.length > 0;

  const priceDelta = allAddons.reduce((delta, addon) => {
    const wasActive = initialAddonSet.has(addon.id);
    const isNow = pendingAddonIds.includes(addon.id);
    if (isNow && !wasActive) return delta + addon.price;
    if (isNow && wasActive && showSeatManagement) {
      const old = currentSeatCounts[addon.id] ?? 0;
      const next = pendingSeatCounts[addon.id] ?? 0;
      return delta + (next - old) * addon.price;
    }
    return delta;
  }, 0);

  const handleAddModule = (addonId: string) => {
    setPendingAddonIds((prev) => [...prev, addonId]);
    if (showSeatManagement) {
      setPendingSeatCounts((prev) => ({
        ...prev,
        [addonId]: Math.max(1, currentSeatCounts[addonId] ?? 1),
      }));
    }
  };

  const handleSeatStep = (addonId: string, direction: 1 | -1) => {
    const summary = seatSummaries.find((s) => s.addonId === addonId);
    const minSeats = summary?.assignedCount ?? 0;
    setPendingSeatCounts((prev) => ({
      ...prev,
      [addonId]: Math.max(minSeats, (prev[addonId] ?? 1) + direction),
    }));
  };

  const handleApply = async () => {
    try {
      setApplyError(null);
      await onApplyChanges(pendingAddonIds, pendingSeatCounts);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setApplyError(apiError.message || 'Failed to apply changes. Please try again.');
    }
  };

  const handleDiscard = () => {
    setPendingAddonIds(activeAddonIds);
    setPendingSeatCounts(currentSeatCounts);
    setApplyError(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="warning"
        action={
          <Button size="small" onClick={loadAddons}>
            Retry
          </Button>
        }
      >
        {loadError}
      </Alert>
    );
  }

  return (
    <Box>
      {applyError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApplyError(null)}>
          {applyError}
        </Alert>
      )}

      {/* Active modules */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 1.2,
            color: 'text.secondary',
            fontSize: 11,
          }}
        >
          Your Modules
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
          To remove a module, contact support
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mb: currentModules.length > 0 && availableToAdd.length > 0 ? 3 : 1.5,
        }}
      >
        {currentModules.length === 0 ? (
          <Box
            sx={{
              py: 3,
              px: 2.5,
              borderRadius: 2,
              bgcolor: 'grey.50',
              border: '1px dashed',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No active modules. Add a module below to access CPE content.
            </Typography>
          </Box>
        ) : (
          currentModules.map((addon) => {
            const meta = getAddonMeta(addon.id);
            const { IconComponent } = meta;
            const isBeingAdded = !initialAddonSet.has(addon.id);
            const summary = seatSummaries.find((s) => s.addonId === addon.id);
            const seatCount = pendingSeatCounts[addon.id];
            const assignedCount = summary?.assignedCount ?? 0;
            const seatPct = seatCount ? Math.min(100, (assignedCount / seatCount) * 100) : 0;
            const atMin = showSeatManagement && seatCount !== undefined && seatCount <= assignedCount;

            return (
              <Box
                key={addon.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: { xs: 1.75, sm: 2 },
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: isBeingAdded ? 'primary.light' : 'divider',
                  borderLeft: '3px solid',
                  borderLeftColor: isBeingAdded ? 'primary.main' : 'success.main',
                  bgcolor: isBeingAdded ? 'rgba(86,151,55,0.03)' : 'background.paper',
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    flexShrink: 0,
                    bgcolor: isBeingAdded ? 'rgba(86,151,55,0.1)' : 'rgba(46,125,50,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconComponent
                    sx={{ fontSize: 20, color: isBeingAdded ? 'primary.main' : 'success.main' }}
                  />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: showSeatManagement && seatCount !== undefined ? 0.5 : 0,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {meta.label}
                    </Typography>
                    {isBeingAdded ? (
                      <Chip
                        label="Adding"
                        size="small"
                        color="primary"
                        sx={{ height: 18, fontSize: 10, fontWeight: 700, flexShrink: 0 }}
                      />
                    ) : (
                      <Chip
                        label="Active"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600, flexShrink: 0 }}
                      />
                    )}
                  </Box>
                  {showSeatManagement && seatCount !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 80,
                          height: 4,
                          bgcolor: 'grey.100',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            bgcolor: seatPct > 80 ? 'warning.main' : 'success.light',
                            width: `${seatPct}%`,
                            borderRadius: 2,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {assignedCount}/{seatCount} seats used
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0 }}>
                  {showSeatManagement && seatCount !== undefined && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip
                        title={
                          atMin
                            ? `Can't go below ${assignedCount} (seats currently in use)`
                            : 'Remove a seat'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSeatStep(addon.id, -1)}
                            disabled={!!atMin}
                            sx={{
                              width: 26,
                              height: 26,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                            }}
                          >
                            <RemoveIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ minWidth: 28, textAlign: 'center' }}
                      >
                        {seatCount}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleSeatStep(addon.id, 1)}
                        sx={{
                          width: 26,
                          height: 26,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  )}

                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    ${addon.price.toFixed(0)}/yr
                  </Typography>

                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Available to add */}
      {availableToAdd.length > 0 && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1.2,
              color: 'text.secondary',
              fontSize: 11,
              display: 'block',
              mb: 1.5,
            }}
          >
            Available to Add
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {availableToAdd.map((addon) => {
              const meta = getAddonMeta(addon.id);
              const { IconComponent } = meta;
              return (
                <Box
                  key={addon.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: { xs: 1.75, sm: 2 },
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      flexShrink: 0,
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <IconComponent sx={{ fontSize: 20, color: 'text.disabled' }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {meta.label}
                    </Typography>
                    {meta.description && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {meta.description}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="text.secondary"
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      ${addon.price.toFixed(0)}/yr
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddModule(addon.id)}
                      disableElevation
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Pending changes summary */}
      <Collapse in={hasChanges}>
        <Box
          sx={{
            mt: 3,
            p: 2.5,
            borderRadius: 2,
            bgcolor: 'rgba(86,151,55,0.04)',
            border: '1px solid rgba(86,151,55,0.2)',
          }}
        >
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1.5 }}>
            Pending Changes
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
            {addedIds.map((id) => {
              const meta = getAddonMeta(id);
              const addon = allAddons.find((a) => a.id === id);
              return (
                <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="success.dark" fontWeight={600}>
                    + {meta.label}
                  </Typography>
                  {addon && (
                    <Typography variant="caption" color="success.dark" fontWeight={600}>
                      +${addon.price.toFixed(0)}/yr
                    </Typography>
                  )}
                </Box>
              );
            })}
            {seatChanges.map(([id, newCount]) => {
              const meta = getAddonMeta(id);
              const addon = allAddons.find((a) => a.id === id);
              const oldCount = currentSeatCounts[id] ?? 0;
              const diff = newCount - oldCount;
              const priceDiff = addon ? diff * addon.price : 0;
              return (
                <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {meta.label}: {oldCount} → {newCount} seats
                  </Typography>
                  {priceDiff !== 0 && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color={priceDiff > 0 ? 'success.dark' : 'error.main'}
                    >
                      {priceDiff > 0 ? '+' : ''}${Math.abs(priceDiff).toFixed(0)}/yr
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing={0.8}
                fontSize={10}
                display="block"
              >
                Annual Change
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{ lineHeight: 1.1, color: priceDelta < 0 ? 'error.main' : 'text.primary' }}
              >
                {priceDelta >= 0 ? '+' : '−'}${Math.abs(priceDelta).toFixed(0)}/yr
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="text"
                size="small"
                onClick={handleDiscard}
                disabled={isApplying}
                sx={{ color: 'text.secondary' }}
              >
                Discard
              </Button>
              <Button
                variant="contained"
                size="medium"
                endIcon={!isApplying ? <ArrowForwardIcon /> : undefined}
                onClick={handleApply}
                disabled={isApplying}
                disableElevation
                sx={{ minWidth: 160, py: 1 }}
              >
                {isApplying ? <CircularProgress size={20} color="inherit" /> : 'Review & Checkout'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
